// Display helpers shared by every page.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export function fmtCurrency(val: string | number | null | undefined, decimals = 2): string {
  if (val === null || val === undefined || val === '') return '—';
  const n = typeof val === 'number' ? val : parseFloat(val);
  return isNaN(n)
    ? '—'
    : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

// Compact for KPI tiles: ₹1.25 L, ₹3.4 Cr.
export function fmtCompact(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(2)} L`;
  if (abs >= 1_000) return `${sign}₹${(abs / 1_000).toFixed(1)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
}

export function fmtNum(val: string | number, maxDecimals = 3): string {
  const n = typeof val === 'number' ? val : parseFloat(val);
  return isNaN(n) ? '—' : n.toLocaleString('en-IN', { maximumFractionDigits: maxDecimals });
}

// '2026-10-03' → '03 Oct 2026'
export function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

// '2026-10' → 'October 2026'
export function fmtMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS_LONG[m - 1]} ${y}`;
}

// Local (not UTC) calendar date.
export function todayISO(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function currentMonth(): string {
  return todayISO().slice(0, 7);
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthBounds(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

// Indian financial year (Apr–Mar) containing a date, e.g. '2026-27'.
export function financialYear(iso = todayISO()): string {
  const [y, m] = iso.split('-').map(Number);
  const start = m >= 4 ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

export function fyBounds(fy: string): { from: string; to: string } {
  const start = parseInt(fy.slice(0, 4), 10);
  return { from: `${start}-04-01`, to: `${start + 1}-03-31` };
}

export function recentFinancialYears(count = 5): string[] {
  const current = parseInt(financialYear().slice(0, 4), 10);
  return Array.from({ length: count }, (_, i) => {
    const s = current - i;
    return `${s}-${String((s + 1) % 100).padStart(2, '0')}`;
  });
}

export function titleCase(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Form values → API payload: '' becomes null so optional fields can be cleared.
export function blankToNull<T extends Record<string, unknown>>(obj: T): { [K in keyof T]: T[K] | null } {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) out[k] = typeof v === 'string' && v.trim() === '' ? null : v;
  return out as { [K in keyof T]: T[K] | null };
}

export function toNum(val: string): number | undefined {
  if (val === '' || val === null || val === undefined) return undefined;
  const n = parseFloat(val);
  return isNaN(n) ? undefined : n;
}

export const PAYMENT_MODES = [
  { value: 'bank_transfer', label: 'Bank Transfer (NEFT/IMPS/RTGS)' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'auto_debit', label: 'Auto Debit' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

export function paymentModeLabel(mode: string | null | undefined): string {
  if (!mode) return '—';
  return PAYMENT_MODES.find((m) => m.value === mode)?.label.replace(/ \(.*\)$/, '') ?? titleCase(mode);
}
